import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';

const LoginContainer = styled.div`
  max-width: 400px;
  margin: 40px auto;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 2rem;
  color: #2d3748;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #4a5568;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  
  -webkit-text-security: none !important;

  &[type="password"] {
    -webkit-text-security: disc !important;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary || '#a0522d'};
    box-shadow: 0 0 0 3px ${props => props.theme.primary ? `${props.theme.primary}1A` : 'rgba(160, 82, 45, 0.1)'};
  }
`;

const Button = styled.button`
  padding: 0.75rem;
  background-color: ${props => props.theme.primary || '#a0522d'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    filter: brightness(0.9);
  }

  &:disabled {
    background-color: #cbd5e0;
    cursor: not-allowed;
  }
`;

const RegisterLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.9rem;
  color: #718096;

  a {
    color: ${props => props.theme.primary || '#a0522d'};
    font-weight: 600;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${props => props.theme.primary || '#a0522d'};
  font-weight: 600;
  font-size: inherit;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const PasswordWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const EyeButton = styled.button`
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  transition: color 0.2s;

  &:hover {
    color: #4a5568;
  }

  i {
    font-size: 1.1rem;
  }
`;

const LoginPage: React.FC = () => {
  const [cpfOrEmail, setCpfOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, setShowForgotPasswordModal, setShowFirstAccessModal } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const success = await login({ cpfOrPhone: cpfOrEmail, password });

    if (success) {
      toast.success('Bem-vindo!');
      navigate(-1); // Go back to the previous page
    } else {
      toast.error('Celular/CPF ou senha incorretos. Por favor, tente novamente.');
    }
    setIsLoading(false);
  };

  return (
    <Layout>
      <LoginContainer>
        <Title>Entrar</Title>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="cpfOrEmail">Celular ou CPF</Label>
            <Input
              id="cpfOrEmail"
              type="text"
              value={cpfOrEmail}
              onChange={(e) => setCpfOrEmail(e.target.value)}
              placeholder="Digite seu celular ou CPF"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="password">Senha</Label>
            <PasswordWrapper>
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite sua senha"
                required
                style={{ paddingRight: '2.5rem' }}
              />
              <EyeButton type="button" onClick={() => setShowPassword(!showPassword)}>
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </EyeButton>
            </PasswordWrapper>
          </FormGroup>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Entrando...' : 'Entrar'}
          </Button>
        </Form>
        <RegisterLink>
          Primeiro acesso?{' '}
          <LinkButton type="button" onClick={() => setShowFirstAccessModal(true)}>
            Criar senha
          </LinkButton>
        </RegisterLink>
        <RegisterLink>
          Esqueceu a senha?{' '}
          <LinkButton type="button" onClick={() => setShowForgotPasswordModal(true)}>
            Recuperar senha
          </LinkButton>
        </RegisterLink>
        <RegisterLink>
          Não tem uma conta? <Link to="/registrar">Cadastre-se</Link>
        </RegisterLink>
      </LoginContainer>
    </Layout>
  );
};

export default LoginPage;
